// Sections 9, 10 and 18 (stream M3a): Build tab and Tools panel actions. Each edits the decision
// draft and is allowed only while deciding (24.7). Build commands are appended in order and the
// decide phase applies them in that order; a toggle that undoes an earlier command in this draft
// removes that command instead of stacking another.
import type {
  BuildCommand,
  BuildingId,
  BuildingTypeId,
  BuildOrderId,
  Decision,
  HousingPolicy,
  PersonId,
  ToolAllocationOption,
} from '../../sim/api';
import { withBoundary } from '../boundary';
import { requireGame, requireStatus } from '../guards';
import { draft, patchUi } from '../state';

function edit(change: (d: Decision) => Decision): void {
  requireStatus('deciding');
  const current = draft.value;
  if (current === null) return;
  draft.value = change(current);
  patchUi({ problems: [] });
}

const sameOrder =
  (kind: BuildCommand['kind'], orderId: BuildOrderId) =>
  (c: BuildCommand): boolean =>
    c.kind === kind && 'orderId' in c && c.orderId === orderId;

/** Section 9: add a new building to the end of the queue. */
export const queueBuild = withBoundary((buildingType: BuildingTypeId): void => {
  edit((d) => ({ ...d, builds: [...d.builds, { kind: 'queue', buildingType, repairOf: null }] }));
});

/**
 * Section 9: cancel a queued order (materials already paid come back at 50%). Calling it again
 * this season takes the cancellation back.
 */
export const cancelBuild = withBoundary((orderId: BuildOrderId): void => {
  edit((d) => {
    const cancelled = d.builds.some(sameOrder('cancel', orderId));
    return {
      ...d,
      builds: cancelled
        ? d.builds.filter((c) => !sameOrder('cancel', orderId)(c))
        : [...d.builds, { kind: 'cancel', orderId }],
    };
  });
});

/** Drop a building queued this season (the `index`-th queue command of the draft). */
export const unqueueBuild = withBoundary((index: number): void => {
  edit((d) => {
    let seen = -1;
    return {
      ...d,
      builds: d.builds.filter((c) => {
        if (c.kind !== 'queue') return true;
        seen += 1;
        return seen !== index;
      }),
    };
  });
});

/** Section 9: pause or resume an order; builders skip paused items. */
export const pauseBuild = withBoundary((orderId: BuildOrderId, paused: boolean): void => {
  const state = requireGame();
  edit((d) => {
    const builds = d.builds.filter((c) => !sameOrder('pause', orderId)(c));
    const standing = state.buildQueue.find((o) => o.id === orderId)?.paused ?? false;
    return {
      ...d,
      builds: standing === paused ? builds : [...builds, { kind: 'pause', orderId, paused }],
    };
  });
});

/** Section 9: move an order to `toIndex` in the queue (the player orders the queue). */
export const moveBuild = withBoundary((orderId: BuildOrderId, toIndex: number): void => {
  edit((d) => ({
    ...d,
    builds: [...d.builds, { kind: 'move', orderId, toIndex: Math.max(0, Math.floor(toIndex)) }],
  }));
});

/** Section 9: queue a repair that restores a building to full condition. */
export const queueRepair = withBoundary((buildingId: BuildingId): void => {
  const state = requireGame();
  const building = state.buildings[buildingId];
  if (building === undefined) return;
  edit((d) => {
    const queued =
      state.buildQueue.some((o) => o.repairOf === buildingId) ||
      d.builds.some((c) => c.kind === 'queue' && c.repairOf === buildingId);
    if (queued) return d;
    return {
      ...d,
      builds: [...d.builds, { kind: 'queue', buildingType: building.type, repairOf: buildingId }],
    };
  });
});

/** Section 18: tool allocation policy (a change takes effect for the season being decided). */
export const setToolAllocation = withBoundary((option: ToolAllocationOption): void => {
  const state = requireGame();
  edit((d) => {
    const policies = { ...d.policies };
    if (state.policies.toolAllocation === option) delete policies.toolAllocation;
    else policies.toolAllocation = option;
    return { ...d, policies };
  });
});

/**
 * Section 10 manual allocation: give (`true`) or withhold (`false`) a tool for this person, or
 * `null` to clear the pin (a standing pin is cleared by withholding, as the Decision can only
 * set pins).
 */
export const setToolOverride = withBoundary((personId: PersonId, has: boolean | null): void => {
  const state = requireGame();
  edit((d) => {
    const toolOverrides = { ...d.toolOverrides };
    const standing = state.toolOverrides[personId];
    if (has === null) {
      if (standing === true) toolOverrides[personId] = false;
      else delete toolOverrides[personId];
    } else if (standing === has || (standing === undefined && !has)) {
      delete toolOverrides[personId];
    } else {
      toolOverrides[personId] = has;
    }
    return { ...d, toolOverrides };
  });
});

/** Section 9 and 18: housing policy (mode, allow overcrowding, split households). */
export const setHousingPolicy = withBoundary((policy: HousingPolicy): void => {
  const state = requireGame();
  edit((d) => {
    const policies = { ...d.policies };
    if (JSON.stringify(state.policies.housing) === JSON.stringify(policy)) delete policies.housing;
    else policies.housing = policy;
    return { ...d, policies };
  });
});
